import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import EmbeddedStripePaymentElement from './components/EmbeddedStripePaymentElement';
import { useSearchParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Mail,
  Phone,
  FileText,
  CreditCard,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Car,
  Zap,
  ShieldCheck,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Step indicator - dark theme
const StepIndicator = ({ currentStep, steps }) => (
  <div className="flex items-center justify-between w-full mb-6">
    {steps.map((step, index) => (
      <React.Fragment key={step.id}>
        <div className="flex flex-col items-center shrink-0">
          <div
            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-medium text-xs sm:text-sm transition-all ${
              index < currentStep
                ? 'bg-emerald-500 text-white'
                : index === currentStep
                ? 'bg-[#ED1C24] text-white'
                : 'bg-white/20 text-white/60'
            }`}
          >
            {index < currentStep ? <CheckCircle className="w-4 h-4" /> : index + 1}
          </div>
          <span className={`text-[10px] sm:text-xs mt-1 whitespace-nowrap ${index <= currentStep ? 'text-white font-medium' : 'text-white/50'}`}>
            {step.label}
          </span>
        </div>
        {index < steps.length - 1 && (
          <div className={`flex-1 h-0.5 mx-1.5 sm:mx-2 -mt-4 ${index < currentStep ? 'bg-emerald-500' : 'bg-white/20'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Notary type card - dark theme
const NotaryTypeCard = ({ type, selected, onSelect, disabled }) => (
  <button
    onClick={() => !disabled && onSelect(type)}
    disabled={disabled}
    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
      selected
        ? 'border-[#ED1C24] bg-[#ED1C24]/20'
        : disabled
        ? 'border-white/10 bg-white/5 opacity-50 cursor-not-allowed'
        : 'border-white/20 hover:border-white/40 hover:bg-white/10 bg-white/5'
    }`}
    data-testid={`notary-type-${type.id}`}
  >
    <div className="flex justify-between items-center">
      <div className="flex-1 min-w-0">
        <h3 className={`text-sm font-semibold ${selected ? 'text-[#ED1C24]' : 'text-white'}`}>
          {type.name}
        </h3>
        <p className="text-[10px] text-white/60 mt-0.5 uppercase tracking-wide">{type.description}</p>
      </div>
      <div className="flex items-center gap-2 ml-3">
        <span className={`text-base font-bold ${selected ? 'text-[#ED1C24]' : 'text-white'}`}>
          ${type.price.toFixed(0)}
        </span>
      </div>
    </div>
  </button>
);

// Calendar component - dark theme
const BookingCalendar = ({ selectedDate, onSelectDate, notaryType }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const month = currentMonth.getMonth() + 1;
      const year = currentMonth.getFullYear();
      let url = `${API_URL}/api/notary/calendar-dates?month=${month}&year=${year}`;
      if (notaryType) url += `&notary_type=${notaryType}`;

      // Retry once on transient failure (mobile networks + cold starts can drop the first call)
      let response;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          response = await fetch(url, { cache: 'no-store' });
          if (response.ok) break;
        } catch (err) {
          if (attempt === 1) throw err;
        }
        await new Promise(r => setTimeout(r, 400));
      }

      if (response && response.ok) {
        const data = await response.json();
        setCalendarData(data);
      }
    } catch (err) {
      console.error('Failed to fetch calendar:', err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, notaryType]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const isAllDaysService = notaryType && (notaryType.includes('mortgage') || notaryType.includes('loan'));
  const isWeekendService = notaryType && notaryType.includes('weekend');

  const getDayClass = (dateInfo) => {
    const isSelected = selectedDate === dateInfo.date;
    
    if (dateInfo.is_past || !dateInfo.available) {
      return 'text-white/20 cursor-not-allowed';
    }
    if (isSelected) {
      return 'bg-[#ED1C24] text-white font-bold';
    }
    if (dateInfo.is_holiday) {
      return 'bg-amber-500/30 text-amber-200 hover:bg-amber-500/50 cursor-pointer';
    }
    if (isAllDaysService) {
      return 'bg-white/10 hover:bg-white/20 cursor-pointer text-white';
    }
    if (isWeekendService && dateInfo.is_weekend) {
      return 'bg-blue-500/30 text-blue-200 hover:bg-blue-500/50 cursor-pointer';
    }
    return 'bg-white/10 hover:bg-white/20 cursor-pointer text-white';
  };

  const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="bg-white/5 rounded-lg border border-white/20 p-3">
      <div className="flex items-center justify-between mb-2">
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-1.5 hover:bg-white/10 rounded text-white">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h3 className="font-semibold text-sm text-white">
          {calendarData?.month_name} {calendarData?.year}
        </h3>
        <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-1.5 hover:bg-white/10 rounded text-white">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {daysOfWeek.map((day, i) => (
          <div key={i} className="text-center text-[10px] font-semibold text-white/50 py-1">
            {day}
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-white/50" />
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          {calendarData?.dates.map(dateInfo => (
            <button
              key={dateInfo.date}
              onClick={() => dateInfo.available && !dateInfo.is_past && onSelectDate(dateInfo.date)}
              disabled={dateInfo.is_past || !dateInfo.available}
              className={`aspect-square rounded text-xs transition-all ${getDayClass(dateInfo)}`}
              title={dateInfo.holiday_name || ''}
            >
              {dateInfo.day}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Time slot picker - dark theme
const TimeSlotPicker = ({ slots, selectedTime, onSelectTime }) => {
  const availableSlots = slots?.filter(s => s.available) || [];
  
  return (
    <div className="bg-white/5 rounded-lg border border-white/20 p-3">
      <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-white">
        <Clock className="w-4 h-4" />
        Select Time
      </h3>
      
      {availableSlots.length === 0 ? (
        <p className="text-white/50 text-sm text-center py-4">No available times for this date</p>
      ) : (
        <div className="grid grid-cols-4 gap-1 max-h-48 overflow-y-auto">
          {slots?.map(slot => (
            <button
              key={slot.time}
              onClick={() => slot.available && onSelectTime(slot.time)}
              disabled={!slot.available}
              className={`py-1.5 px-1 rounded text-xs font-medium transition-all ${
                selectedTime === slot.time
                  ? 'bg-[#ED1C24] text-white'
                  : slot.available
                  ? 'bg-white/10 border border-white/20 hover:bg-white/20 text-white'
                  : 'bg-white/5 text-white/20 cursor-not-allowed line-through'
              }`}
            >
              {slot.display_time}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Embeddable Booking Widget - dark theme (for use within /notary page)
export const NotaryBookingWidget = () => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const [lane, setLane] = useState(null);
  const [docType, setDocType] = useState(null);
  const [customDoc, setCustomDoc] = useState('');
  const [quote, setQuote] = useState(null);

  const DOCUMENTS = [
    { name: 'Affidavit', lane: 'general' },
    { name: 'Power of Attorney', lane: 'general' },
    { name: 'Real Estate Deed (Warranty / Quitclaim)', lane: 'general' },
    { name: 'Deed of Trust', lane: 'general' },
    { name: 'Promissory Note', lane: 'general' },
    { name: 'Will or Trust', lane: 'general' },
    { name: 'Divorce / Family Law Document', lane: 'general' },
    { name: 'Business / Corporate Document', lane: 'general' },
    { name: 'Advance Healthcare Directive', lane: 'general' },
    { name: 'Minor Travel Consent (Passport)', lane: 'general' },
    { name: 'Real Estate Closing Packet (Buying or Selling)', lane: 'mortgage' },
    { name: 'Other', lane: 'general', other: true },
  ];
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    address2: '',
    notes: ''
  });
  const addressInputRef = useRef(null);
  
  const [notaryTypes, setNotaryTypes] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [mileageInfo, setMileageInfo] = useState(null);
  
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [loadingMileage, setLoadingMileage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [holidayInfo, setHolidayInfo] = useState(null);

  const steps = [
    { id: 'service', label: 'Service' },
    { id: 'datetime', label: 'Date/Time' },
    { id: 'fees', label: 'Fees' },
    { id: 'details', label: 'Details' },
    { id: 'payment', label: 'Pay' }
  ];

  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedDate || !lane) return;
      setLoadingSlots(true);
      try {
        const response = await fetch(
          `${API_URL}/api/notary/availability?date_str=${selectedDate}&lane=${lane}`
        );
        if (response.ok) {
          const data = await response.json();
          setTimeSlots(data.slots);
        }
      } catch (err) {
        console.error('Failed to fetch time slots:', err);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [selectedDate, lane]);

  useEffect(() => {
    const fetchQuote = async () => {
      if (!lane || !selectedDate || !selectedTime) { setQuote(null); return; }
      try {
        const res = await fetch(`${API_URL}/api/notary/quote?lane=${lane}&date_str=${selectedDate}&time_str=${encodeURIComponent(selectedTime)}`);
        if (res.ok) setQuote(await res.json());
      } catch (err) {
        console.error('Failed to fetch quote:', err);
      }
    };
    fetchQuote();
  }, [lane, selectedDate, selectedTime]);

  const calculateMileage = async (address) => {
    if (!address || address.length < 10) {
      setMileageInfo(null);
      return;
    }
    setLoadingMileage(true);
    try {
      const response = await fetch(`${API_URL}/api/notary/calculate-mileage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address })
      });
      if (response.ok) {
        const data = await response.json();
        setMileageInfo(data);
      }
    } catch (err) {
      console.error('Failed to calculate mileage:', err);
    } finally {
      setLoadingMileage(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerInfo.address) calculateMileage(customerInfo.address);
    }, 500);
    return () => clearTimeout(timer);
  }, [customerInfo.address]);

  // Google Places Autocomplete on the meeting-address field, biased to Lubbock
  useEffect(() => {
    let interval;
    const init = () => {
      if (!addressInputRef.current || !window.google?.maps?.places) return;
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        componentRestrictions: { country: 'us' },
        fields: ['formatted_address', 'geometry'],
      });
      autocomplete.setBounds(
        new window.google.maps.LatLngBounds(
          new window.google.maps.LatLng(31.8, -103.5),
          new window.google.maps.LatLng(35.2, -100.0)
        )
      );
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const formatted = place?.formatted_address || addressInputRef.current.value;
        if (formatted) {
          setCustomerInfo(prev => ({ ...prev, address: formatted }));
        }
      });
    };
    if (window.google?.maps?.places) {
      init();
    } else {
      interval = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(interval); init(); }
      }, 250);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [currentStep]);  // re-init when entering the Details step where the field exists

  const [payIntent, setPayIntent] = useState(null);  // { client_secret, payment_intent_id, booking_id, total_price, ... }

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/notary/book-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lane: lane,
          appointment_date: selectedDate,
          appointment_time: selectedTime,
          first_name: customerInfo.firstName,
          last_name: customerInfo.lastName,
          email: customerInfo.email,
          phone: customerInfo.phone,
          address: customerInfo.address,
          address_2: customerInfo.address2 || null,
          document_type: docType?.other ? customDoc.trim() : docType?.name,
          notes: [
            (docType?.other ? customDoc.trim() : docType?.name) ? `Document: ${docType?.other ? customDoc.trim() : docType?.name}` : '',
            customerInfo.notes
          ].filter(Boolean).join('\n')
        })
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = typeof data.detail === 'string'
          ? data.detail
          : (data.detail?.message || data.message || 'Failed to create booking. Please try again.');
        throw new Error(errorMessage);
      }
      if (!data.client_secret) throw new Error('No payment session returned. Please try again.');
      setPayIntent(data);
    } catch (err) {
      const errorMsg = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Called by the Payment Element after intent.status === 'succeeded'
  const handlePaymentSuccess = async () => {
    if (!payIntent) return;
    try {
      const res = await fetch(`${API_URL}/api/notary/booking/${payIntent.booking_id}/confirm-intent?payment_intent_id=${encodeURIComponent(payIntent.payment_intent_id)}`, { method: 'POST' });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not confirm booking');
      }
      // Redirect to confirmation page (existing /notary/confirmation reads booking_id)
      window.location.href = `/notary/confirmation?booking_id=${payIntent.booking_id}`;
    } catch (e) {
      setError(e.message || 'Booking confirmation failed. Please contact support.');
    }
  };

  const basePrice = quote?.price || 0;
  const mileageFee = mileageInfo?.mileage_fee || 0;
  const totalPrice = basePrice + (mileageFee > 0 ? mileageFee : 0);

  const canProceed = () => {
    switch (currentStep) {
      case 0: return docType !== null && (!docType.other || customDoc.trim().length > 0);
      case 1: return selectedDate && selectedTime;
      case 2: return quote !== null;
      case 3: {
        const outsideServiceArea = mileageInfo && mileageInfo.mileage_fee === -1;
        // Validate phone number has at least 10 digits
        const phoneDigits = customerInfo.phone.replace(/\D/g, '');
        const isValidPhone = phoneDigits.length >= 10;
        return customerInfo.firstName && customerInfo.lastName && customerInfo.email && isValidPhone && customerInfo.address && !outsideServiceArea;
      }
      default: return true;
    }
  };

  const handleNext = () => { if (canProceed()) setCurrentStep(prev => Math.min(prev + 1, steps.length - 1)); };
  const handleBack = () => { setCurrentStep(prev => Math.max(prev - 1, 0)); };

  // Common input styles for dark theme
  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.3)',
    color: '#ffffff'
  };

  return (
    <div className="rounded-xl p-4 sm:p-6 w-full border border-white/20 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)' }}>
      {error && (
        <div className="mb-4 bg-red-500/20 border border-red-500/50 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
          <span className="text-red-200 flex-1 text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300"><X className="w-5 h-5" /></button>
        </div>
      )}

      <StepIndicator currentStep={currentStep} steps={steps} />

      <div className="min-h-[400px]">
        {/* Step 1: Document selection */}
        {currentStep === 0 && (
          <div>
            <h2 className="text-base font-semibold text-white mb-4 flex items-center justify-center gap-2 text-center">
              <FileText className="w-5 h-5 text-[#ED1C24]" />
              What do you need notarized?
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {DOCUMENTS.map(d => {
                const active = docType?.name === d.name;
                return (
                  <button
                    key={d.name}
                    type="button"
                    onClick={() => { setDocType(d); setLane(d.lane); if (!d.other) setCustomDoc(''); }}
                    data-testid={`notary-doc-${d.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                    className={`flex items-center justify-between text-left rounded-lg px-4 py-3 border text-sm transition-colors ${active ? 'border-[#ED1C24] bg-[#ED1C24]/10 text-white' : 'border-white/15 bg-white/5 text-white/80 hover:border-white/35'}`}
                  >
                    <span>{d.name}</span>
                    {active && <CheckCircle className="w-4 h-4 text-[#ED1C24] shrink-0 ml-2" />}
                  </button>
                );
              })}
            </div>
            {docType?.other && (
              <div className="mt-3">
                <label className="block text-xs text-white/50 mb-1">Tell us what you need notarized</label>
                <input
                  type="text"
                  value={customDoc}
                  onChange={(e) => setCustomDoc(e.target.value)}
                  data-testid="notary-doc-other-input"
                  placeholder="e.g., Vehicle title transfer"
                  className="w-full rounded-lg bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#ED1C24] focus:outline-none"
                />
              </div>
            )}
          </div>
        )}

        {/* Step 2: Date & Time */}
        {currentStep === 1 && (
          <div>
            <h2 className="text-base font-semibold text-white mb-1 flex items-center justify-center gap-2 text-center">
              <Calendar className="w-5 h-5 text-[#ED1C24]" />
              Choose Date & Time
            </h2>
            <p className="text-sm text-white/60 mb-4 text-center">{docType?.other ? customDoc : docType?.name}</p>

            <div className="grid md:grid-cols-2 gap-4">
              <BookingCalendar
                selectedDate={selectedDate}
                onSelectDate={(date) => { setSelectedDate(date); setSelectedTime(null); }}
              />
              {selectedDate ? (
                loadingSlots ? (
                  <div className="flex items-center justify-center bg-white/5 rounded-lg border border-white/20">
                    <Loader2 className="w-5 h-5 animate-spin text-white/50" />
                  </div>
                ) : (
                  <TimeSlotPicker slots={timeSlots} selectedTime={selectedTime} onSelectTime={setSelectedTime} />
                )
              ) : (
                <div className="flex items-center justify-center bg-white/5 rounded-lg border border-white/20 p-4">
                  <p className="text-white/50 text-sm text-center">Select a date to see available times</p>
                </div>
              )}
            </div>

            {selectedDate && selectedTime && (
              <div className="mt-4 bg-emerald-500/20 border border-emerald-500/50 rounded-lg p-3 text-sm">
                <span className="font-semibold text-emerald-200">
                  ✓ {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} at {timeSlots.find(s => s.time === selectedTime)?.display_time}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Fees */}
        {currentStep === 2 && (
          <div>
            <h2 className="text-base font-semibold text-white mb-4 flex items-center justify-center gap-2 text-center">
              <CreditCard className="w-5 h-5 text-[#ED1C24]" />
              Your Quote
            </h2>
            {quote ? (() => {
              const accent = quote.is_holiday
                ? { name: 'Holiday', ring: 'ED1C24', text: 'text-[#ED1C24]', chipDot: 'bg-[#ED1C24]', glow: 'from-[#ED1C24]/25' }
                : quote.is_rush
                  ? { name: 'Rush', ring: 'ED1C24', text: 'text-[#ED1C24]', chipDot: 'bg-[#ED1C24]', glow: 'from-[#ED1C24]/25' }
                  : { name: 'Routine', ring: '10b981', text: 'text-emerald-400', chipDot: 'bg-emerald-400', glow: 'from-emerald-500/20' };
              return (
              <div
                className="relative rounded-2xl border border-white/10 bg-[#141821] shadow-2xl shadow-black/40 overflow-hidden"
                data-testid="notary-fee-summary"
              >
                {/* rate-aware top accent bar */}
                <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, #${accent.ring}, #${accent.ring}00)` }} />
                {/* soft glow behind price */}
                <div className={`pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full blur-3xl bg-gradient-to-br ${accent.glow} to-transparent`} />

                <div className="relative px-5 py-4 border-b border-white/10 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Service</div>
                    <div className="text-white font-semibold mt-0.5 truncate">{docType?.other ? customDoc : docType?.name}</div>
                  </div>
                  {(quote.is_holiday || quote.is_rush) && (
                    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.15em] ${accent.text} bg-[#ED1C24]/10 border border-[#ED1C24]/40 rounded-full px-2.5 py-1`}>
                      <Zap className="w-3 h-3" fill="currentColor" />
                      {quote.is_holiday ? 'Holiday' : 'Rush'}
                    </span>
                  )}
                </div>

                <div className="relative px-5 py-7 flex items-end justify-between gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mb-1.5">Service fee</div>
                    <div className="flex items-start gap-1 leading-none" data-testid="notary-fee-amount">
                      <span className="text-2xl font-semibold text-white/70 mt-1.5">$</span>
                      <span className="text-6xl font-bold text-white tracking-tight">{(quote.price || 0).toFixed(0)}</span>
                    </div>
                  </div>
                  <div className="text-right text-sm space-y-1">
                    <div className="flex items-center justify-end gap-1.5 text-white/85 font-medium">
                      <Calendar className="w-3.5 h-3.5 text-white/40" />
                      {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </div>
                    <div className="flex items-center justify-end gap-1.5 text-white/50">
                      <Clock className="w-3.5 h-3.5 text-white/30" />
                      {timeSlots.find(s => s.time === selectedTime)?.display_time}
                    </div>
                  </div>
                </div>

                <div className="relative px-5 py-3.5 border-t border-white/10 flex items-center gap-2.5" data-testid="notary-fee-reason">
                  <span className={`w-2 h-2 rounded-full ${accent.chipDot} shadow-[0_0_8px_currentColor] ${accent.text}`} />
                  <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">Rate</span>
                  <span className="text-white font-semibold text-sm ml-auto">{quote.category_label || quote.reason}</span>
                </div>

                <div className="relative px-5 py-3.5 border-t border-white/10 flex items-start gap-2.5 text-xs text-white/50 leading-relaxed">
                  <ShieldCheck className={`w-4 h-4 shrink-0 mt-0.5 ${quote.refundable ? 'text-emerald-400' : 'text-white/30'}`} />
                  <span>{quote.refundable ? `Refundable if cancelled ${quote.cancellation_hours}+ hours ahead.` : 'Non-refundable rush service.'} Mileage, if any, is added on the next step once we have your address.</span>
                </div>
              </div>
              );
            })() : (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>
            )}
          </div>
        )}

        {/* Step 4: Customer Details */}
        {currentStep === 3 && (
          <div>
            <h2 className="text-base font-semibold text-white mb-4 flex items-center justify-center gap-2 text-center">
              <User className="w-5 h-5 text-[#ED1C24]" />
              Your Details
            </h2>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={customerInfo.firstName}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, firstName: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                    style={inputStyle}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={customerInfo.lastName}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, lastName: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                    style={inputStyle}
                    placeholder="Smith"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Email *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                  <input
                    type="email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                    style={inputStyle}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Phone *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
                  <input
                    type="tel"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                    style={inputStyle}
                    placeholder="(806) 555-1234"
                  />
                </div>
                {customerInfo.phone && customerInfo.phone.replace(/\D/g, '').length < 10 && (
                  <p className="text-xs text-red-400 mt-1">Please enter a valid 10-digit phone number</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Meeting Address *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 w-4 h-4 text-white/50" />
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={customerInfo.address}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                    className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                    style={inputStyle}
                    placeholder="Start typing… we'll suggest matches"
                    autoComplete="off"
                    data-testid="notary-address"
                  />
                </div>
                <div className="text-[11px] text-white/40 mt-1">Pick a suggestion for the most accurate mileage calculation.</div>
              </div>

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Apt / Suite / Unit <span className="text-white/40 font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={customerInfo.address2}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address2: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                  style={inputStyle}
                  placeholder="e.g. Apt 4B, Suite 200"
                  data-testid="notary-address-2"
                />
              </div>

              {/* Mileage Info */}
              {loadingMileage ? (
                <div className="flex items-center gap-2 text-white/50 text-sm py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Calculating distance...</span>
                </div>
              ) : mileageInfo && (
                <div className={`p-3 rounded-lg flex items-center gap-3 ${
                  mileageInfo.mileage_fee === -1
                    ? 'bg-red-500/20 border border-red-500/50'
                    : mileageInfo.within_free_zone 
                      ? 'bg-emerald-500/20 border border-emerald-500/50' 
                      : 'bg-amber-500/20 border border-amber-500/50'
                }`}>
                  <Car className={`w-5 h-5 shrink-0 ${
                    mileageInfo.mileage_fee === -1 ? 'text-red-400'
                      : mileageInfo.within_free_zone ? 'text-emerald-400' : 'text-amber-400'
                  }`} />
                  <div className="text-sm">
                    <span className={`font-semibold ${
                      mileageInfo.mileage_fee === -1 ? 'text-red-200'
                        : mileageInfo.within_free_zone ? 'text-emerald-200' : 'text-amber-200'
                    }`}>
                      {mileageInfo.mileage_fee === -1
                        ? 'Outside Service Area'
                        : mileageInfo.within_free_zone 
                          ? 'No mileage fee' 
                          : `+$${mileageInfo.mileage_fee.toFixed(2)} mileage fee`}
                    </span>
                    <span className={`ml-2 ${
                      mileageInfo.mileage_fee === -1 ? 'text-red-300'
                        : mileageInfo.within_free_zone ? 'text-emerald-300' : 'text-amber-300'
                    }`}>
                      ({mileageInfo.distance_miles} miles{mileageInfo.mileage_fee === -1 && ' - max 60'})
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-white/70 mb-1">Special Instructions (optional)</label>
                <textarea
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 text-sm rounded-lg focus:ring-2 focus:ring-[#ED1C24] focus:outline-none placeholder-white/40"
                  style={inputStyle}
                  placeholder="Gate code, building instructions, etc."
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Review & Payment */}
        {currentStep === 4 && (
          <div>
            <h2 className="text-base font-semibold text-white mb-4 flex items-center justify-center gap-2 text-center">
              <CreditCard className="w-5 h-5 text-[#ED1C24]" />
              Review & Pay
            </h2>

            <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-3 border border-white/20">
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Service</span>
                <span className="font-semibold text-white">{quote?.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Date & Time</span>
                <span className="font-semibold text-white">
                  {selectedDate && new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {timeSlots.find(s => s.time === selectedTime)?.display_time}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Location</span>
                <span className="font-semibold text-white text-right max-w-[60%]">
                  {customerInfo.address}
                  {customerInfo.address2 && <><br />{customerInfo.address2}</>}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Contact</span>
                <span className="font-semibold text-white">{customerInfo.firstName} {customerInfo.lastName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Email</span>
                <span className="font-semibold text-white">{customerInfo.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/60">Phone</span>
                <span className="font-semibold text-white">{customerInfo.phone}</span>
              </div>
              
              <div className="border-t border-white/20 pt-3 mt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/60">Service Fee</span>
                  <span className="font-semibold text-white">${basePrice.toFixed(2)}</span>
                </div>
                {mileageFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Mileage Fee</span>
                    <span className="font-semibold text-white">${mileageFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-2 border-t border-white/20">
                  <span className="text-white">Total</span>
                  <span className="text-[#ED1C24]">${totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-lg p-3 mb-4 text-sm border border-white/20 text-center">
              <span className="font-semibold text-white/70">Cancellation Policy: </span>
              <span className="text-white/60">
                {quote?.refundable 
                  ? `Refund available if cancelled ${quote.cancellation_hours}+ hours before appointment` 
                  : 'No refunds available for rush service'}
              </span>
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-3 bg-[#ED1C24] text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="pay-now-button"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
              ) : (
                <><CreditCard className="w-5 h-5" /> Pay ${totalPrice.toFixed(2)}</>
              )}
            </button>
            <p className="text-xs text-white/50 text-center mt-2">Secure payment powered by Stripe</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6 pt-4 border-t border-white/20">
        {currentStep > 0 ? (
          <button
            onClick={handleBack}
            className="px-4 py-2 text-sm text-white/70 font-medium hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
        ) : <div />}

        {currentStep < steps.length - 1 && (
          <button
            onClick={handleNext}
            disabled={!canProceed()}
            className="px-5 py-2 bg-[#ED1C24] text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            Continue <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      {payIntent && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '110px 24px 24px' }}
          onClick={() => setPayIntent(null)}
        >
          <div style={{ width: '100%', maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, color: '#fff' }}>
              <div>
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)' }}>
                  Notary Booking
                </div>
                <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                  {payIntent.notary_type_name}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                  {payIntent.appointment_datetime_display}
                </div>
              </div>
              <button onClick={() => setPayIntent(null)} style={{ background: 'transparent', border: '1px solid #292929', color: '#fff', padding: 8, cursor: 'pointer' }} data-testid="notary-pay-close">
                <X size={16} />
              </button>
            </div>
            <div style={{ background: '#0a0a0a', border: '1px solid #292929' }} data-testid="notary-pay-modal">
              <EmbeddedStripePaymentElement
                createIntent={async () => ({
                  client_secret: payIntent.client_secret,
                  payment_intent_id: payIntent.payment_intent_id,
                  amount: payIntent.total_price,
                })}
                returnUrl={`${window.location.origin}/notary/confirmation?booking_id=${payIntent.booking_id}`}
                onSuccess={handlePaymentSuccess}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Standalone page wrapper (for /notary-new route) - keeps light theme
const NotaryBooking = () => {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setError('Booking cancelled. Please try again.');
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="bg-slate-900 py-4 px-4 text-center border-b border-slate-700">
        <h1 className="text-xl font-bold text-white">Schedule Notary</h1>
        <p className="text-slate-400 text-xs mt-1">Mobile Notary Services in Lubbock, TX</p>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <span className="text-red-700 flex-1">{error}</span>
            <button onClick={() => setError(null)}><X className="w-5 h-5 text-red-400" /></button>
          </div>
        )}
        
        <NotaryBookingWidget />
      </div>
    </div>
  );
};

export default NotaryBooking;
